/**
 * src/app/marketplace/components/MarketplaceAccountMenu.js
 * ---------------------------------------------------------------------------
 * Account menu for the site header, carrying the two actions :
 *   Sign out   — ends the browser session; the QuickBooks grant is untouched.
 *   Disconnect — revokes the grant at Intuit QuickBooks.
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useMarketplaceSession from '../hooks/useMarketplaceSession';
import { signOut, disconnect } from '../hooks/marketplaceSessionStore';
import { getProvider } from '../config/providers';
import '../marketplace.css';

export default function MarketplaceAccountMenu() {
	const session = useMarketplaceSession();
	const history = useHistory();

	const [open, setOpen] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [action, setAction] = useState(null);
	const [error, setError] = useState('');

	const containerRef = useRef(null);

	// Dismiss on outside click and on Escape
	useEffect(() => {
		if (!open) return undefined;

		function onPointerDown(event) {
			if (containerRef.current && !containerRef.current.contains(event.target)) close();
		}

		function onKeyDown(event) {
			if (event.key === 'Escape') close();
		}

		document.addEventListener('mousedown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);

		return () => {
			document.removeEventListener('mousedown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	function close() {
		setOpen(false);
		setConfirming(false);
		setAction(null);
		setError('');
	}

	// Nothing to offer an anonymous visitor.
	if (!session.signedIn) return null;

	const provider = getProvider(session.providerKey);

	async function handleDisconnect() {
		setAction('disconnect');
		setError('');

		try {
			await disconnect(provider.disconnectPath);

			close();
			history.push(`/marketplace/${provider.key}/connect`);
		} catch (err) {
			setError(err.response?.data?.error || `Couldn't disconnect ${provider.shortLabel}. Please try again.`);
			setAction(null);
			setConfirming(false);
		}
	}

	async function handleSignOut() {
		setAction('signout');

		try {
			await signOut();

			close();
			history.push(`/marketplace/${provider.key}/connect`);
		} catch (err) {
			setAction(null);
		}
	}

	return (
		<div className="mp-menu" ref={containerRef}>
			<button
				type="button"
				className="mp-menu__trigger"
				onClick={() => (open ? close() : setOpen(true))}
				aria-expanded={open}
				aria-haspopup="true"
			>
				<span
					className={`mp-menu__dot ${session.connected ? 'is-connected' : 'is-disconnected'}`}
					aria-hidden="true"
				/>
				<span className="mp-menu__label">{session.email || 'Account'}</span>
				<svg className="mp-menu__chevron" viewBox="0 0 24 24" aria-hidden="true">
					<path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>

			{open && (
				<div className="mp-menu__panel" role="menu">
					<div className="mp-menu__status">
						<strong>{session.connected ? `${provider.label} connected` : `${provider.label} not connected`}</strong>
						{ session.connected && session.companyName && (
							<span className="mp-menu__status-company">{session.companyName}</span>
						)}
						{session.email && <span className="mp-menu__status-email">{session.email}</span>}
					</div>

					{action ? (
						<p className="mp-menu__progress" role="status" aria-live="polite">
							{action === 'disconnect' ? `Disconnecting ${provider.shortLabel}…` : 'Signing out…'}
						</p>
					) : (
						<>
							{session.connected && !confirming && (
								<button type="button" className="mp-menu__item" role="menuitem" onClick={() => setConfirming(true)}>
									Disconnect {provider.shortLabel}
								</button>
							)}

							{session.connected && confirming && (
								<div className="mp-menu__confirm">
									<p className="mp-menu__confirm-text">
										Uplyft will stop accessing your {provider.label} data. You can reconnect anytime.
									</p>
									<button type="button" className="mp-menu__item mp-menu__item--danger" onClick={handleDisconnect} autoFocus>
										Yes, disconnect {provider.shortLabel}
									</button>
									<button type="button" className="mp-menu__item" onClick={() => setConfirming(false)}>
										Keep connected
									</button>
								</div>
							)}
							
							{!session.connected && (
								<a className="mp-menu__item" role="menuitem" href={`/marketplace/${provider.key}/connect`}>
									Connect {provider.shortLabel}
								</a>
							)}

							<button type="button" className="mp-menu__item" role="menuitem" onClick={handleSignOut}>
								Sign out
							</button>
						</>
					)}

					<p className="mp-menu__error" role="status" aria-live="polite">{error}</p>
				</div>
			)}
		</div>
	);
}
